import React, { useCallback, useContext, useState } from 'react';

import { useStatsigClient } from '@statsig/react-bindings';
import { Trans, useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';

import CollaboratorsActionsMenu from './CollaboratorsActionsMenu/CollaboratorsActionsMenu';
import { ReactComponent as HappyBuddy } from '../../../../../../../assets/icons/v3/happy-buddy.svg';
import { ReactComponent as SurprisedBuddy } from '../../../../../../../assets/icons/v3/surprised-buddy.svg';
import { DELETE_V3, OPTIONS_ICON } from '../../../../../../constants/images';
import { CERTIFICATE_NOT_FOUND_MODAL } from '../../../../../../constants/modal';
import { PLAN_TYPE_FR, PLAN_TYPE_HEALTH } from '../../../../../../constants/plan';
import { CAN_DOWNLOAD_NOVATION_FG } from '../../../../../../constants/statsig';
import {
  CANCEL_DELETE_COLLABORATOR,
  CLICK_ACTION,
  CONFIRM_DELETE_COLLABORATOR,
  DELETE_COLLABORATOR,
  DOWNLOAD_NOVATION,
  RESEND_EMAIL,
  SECTION_COLLABORATOR_TABLE,
  DOWNLOAD_CERTIFICATE
} from '../../../../../../constants/tagManager';
import {
  DEFAULT_TOAST_DATA,
  DELETE_COLLABORATOR_ERROR_TOAST,
  GENERIC_ERROR_TOAST,
  RESEND_EMAIL_ERROR_TOAST,
  RESEND_EMAIL_SUCCESS_TOAST
} from '../../../../../../constants/toast';
import { CompanyContext } from '../../../../../../context/company/companyContext';
import { PlansContext } from '../../../../../../context/plans/plansContext';
import { IMembersData } from '../../../../../../interfaces/businessData';
import { IInsuranceCertificate } from '../../../../../../interfaces/collaborators';
import { IToast } from '../../../../../../interfaces/toast';
import Companies from '../../../../../../services/api/collaborators/collaborators';
import TagManagerService from '../../../../../../services/gtm/tagManagerService';
import { TRANSLATION_COLLABORATORS } from '../../../../../../translations/collaborators';
import { ModalType } from '../../../../../../types/collaboratorsManagementTypes';
import { GenericFunction } from '../../../../../../types/genericTypes';
import { downloadFile } from '../../../../../../utils/utils';
import BdsTooltip from '../../../../BdsTooltip/BdsTooltip';
import BdsIconButton from '../../../../Buttons/BdsIconButton/BdsIconButton';
import BdsModal from '../../../../Modals/BdsModal/BdsModal';
import LoadingModal from '../../../../Modals/LoadingModal/LoadingModal';
import Toast from '../../../../Toast/Toast';

import './CollaboratorsActions.scss';

interface ICollaboratorsActionsProps {
  member: IMembersData;
  onChange: GenericFunction;
}

const CollaboratorsActions = ({ member, onChange }: ICollaboratorsActionsProps) => {
  const { company, isSpain, isMexico } = useContext(CompanyContext);
  const { plans } = useContext(PlansContext);
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [toastData, setToastData] = useState<IToast>(DEFAULT_TOAST_DATA);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [loadingNovation, setLoadingNovation] = useState<boolean>(false);
  const [loadingCertificates, setLoadingCertificates] = useState<boolean>(false);
  const [certificates, setCertificates] = useState<IInsuranceCertificate[]>([]);
  const plan = member.plans[0];
  const currentPlan = plans?.find((p) => p.id === Number(plan));
  const isRF = currentPlan?.type === PLAN_TYPE_FR;
  const isHealth = currentPlan?.type === PLAN_TYPE_HEALTH;
  const { client } = useStatsigClient();
  const canDownloadNovation = client.checkGate(CAN_DOWNLOAD_NOVATION_FG);
  const hasNovationOption = Boolean(isSpain && isRF && canDownloadNovation);
  const hasInsuranceCertificate = Boolean(isMexico && isHealth);

  const { show } = useIntercom();

  const { t } = useTranslation();

  const closeModal = useCallback(() => {
    setModalType(null);
  }, []);

  const onDelete = useCallback(async () => {
    setLoading(true);
    try {
      await Companies.deleteMembers(company.id, [member.email]);
      onChange(member.email);
    } catch {
      setToastData(DELETE_COLLABORATOR_ERROR_TOAST);
    } finally {
      closeModal();
      setOpenToast(true);
      setLoading(false);
    }
  }, [company.id, member.email, onChange, closeModal]);

  const resendEmail = useCallback(async () => {
    if (!member.email) return;
    setLoading(true);
    try {
      await Companies.resendOnboardingCollaboratorEmails(company.id, [member.email]);
      setToastData(RESEND_EMAIL_SUCCESS_TOAST);
    } catch {
      setToastData(RESEND_EMAIL_ERROR_TOAST);
    } finally {
      closeModal();
      setOpenToast(true);
      setLoading(false);
    }
  }, [company.id, member.email, closeModal]);

  const downloadNovation = useCallback(async () => {
    setLoadingNovation(true);
    try {
      const response = await Companies.downloadNovationAgreement(company.id, member.email);
      TagManagerService.pushEvent(CLICK_ACTION, DOWNLOAD_NOVATION, SECTION_COLLABORATOR_TABLE);
      downloadFile(response.url, true);
    } catch {
      setToastData(GENERIC_ERROR_TOAST);
      setOpenToast(true);
    } finally {
      setLoadingNovation(false);
    }
  }, [company.id, member.email]);

  const fetchInsuranceCertificates = useCallback(async () => {
    setLoadingCertificates(true);
    try {
      const response = await Companies.downloadInsuranceCertificates(company.id, member.email);
      setCertificates(response);
      if (!response.length) {
        setModalType('insuranceCertificate');
      }
    } catch {
      setToastData(GENERIC_ERROR_TOAST);
      setOpenToast(true);
    } finally {
      setLoadingCertificates(false);
    }
  }, [company.id, member.email]);

  const handleCertificateDownload = useCallback((certificate: IInsuranceCertificate) => {
    TagManagerService.pushEvent(CLICK_ACTION, DOWNLOAD_CERTIFICATE, SECTION_COLLABORATOR_TABLE);
    downloadFile(certificate.url, true);
    setIsOptionsMenuOpen(false);
  }, []);

  const toggleOptionsMenu = useCallback(() => {
    setIsOptionsMenuOpen((prevState) => {
      const willOpen = !prevState;
      if (willOpen && hasInsuranceCertificate) {
        fetchInsuranceCertificates();
      }
      return willOpen;
    });
  }, [hasInsuranceCertificate, fetchInsuranceCertificates]);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const handleCellClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const handleMenuOptions = useCallback(
    async (option: string) => {
      switch (option) {
        case 'delete':
          setIsOptionsMenuOpen(false);
          TagManagerService.pushEvent(CLICK_ACTION, DELETE_COLLABORATOR);
          setModalType('delete');
          break;
        case 'resendEmail':
          setIsOptionsMenuOpen(false);
          TagManagerService.pushEvent(CLICK_ACTION, RESEND_EMAIL);
          setModalType('resendEmail');
          break;
        case 'novation':
          setIsOptionsMenuOpen(false);
          downloadNovation();
          break;
      }
    },
    [downloadNovation, setIsOptionsMenuOpen]
  );

  const openIntercom = useCallback(() => {
    show();
    closeModal();
  }, [show, closeModal]);

  const setActionButton = useCallback(() => {
    const isInvited = member.status === 'invited';
    return isInvited || hasNovationOption || hasInsuranceCertificate ? (
      <BdsIconButton
        icon={OPTIONS_ICON}
        action={toggleOptionsMenu}
        variant="transparent"
        size="sm"
        disabled={false}
        label="options"
      />
    ) : (
      <BdsTooltip
        message={TRANSLATION_COLLABORATORS.table.tooltips.deleteCollaborator}
        position="bottom-right"
        variant="standard"
      >
        <BdsIconButton
          icon={DELETE_V3}
          action={() => handleMenuOptions('delete')}
          variant="transparent"
          size="sm"
          label="delete"
          disabled={false}
        />
      </BdsTooltip>
    );
  }, [member.status, toggleOptionsMenu, handleMenuOptions, hasNovationOption, hasInsuranceCertificate]);

  const getModalsDescription = useCallback(
    (key: string) => {
      return (
        <Trans
          i18nKey={key}
          values={{ user_email: member.email }}
          components={{
            b: <b></b>
          }}
        />
      );
    },
    [member.email]
  );

  const getModalProps = useCallback(
    (type: ModalType) => {
      switch (type) {
        case 'delete':
          return {
            open: modalType === type,
            title: t(TRANSLATION_COLLABORATORS.table.deleteModal.title),
            description: getModalsDescription(TRANSLATION_COLLABORATORS.table.deleteModal.description),
            icon: <SurprisedBuddy />,
            loading: loading,
            primaryAction: {
              label: t(TRANSLATION_COLLABORATORS.table.deleteModal.buttonConfirm),
              onClick: () => {
                TagManagerService.pushEvent(CLICK_ACTION, CONFIRM_DELETE_COLLABORATOR);
                onDelete();
              }
            },
            secondaryAction: {
              label: t(TRANSLATION_COLLABORATORS.table.deleteModal.buttonClose),
              onClick: () => {
                TagManagerService.pushEvent(CLICK_ACTION, CANCEL_DELETE_COLLABORATOR);
                closeModal();
              }
            }
          };
        case 'resendEmail':
          return {
            open: modalType === type,
            title: t(TRANSLATION_COLLABORATORS.table.resendEmailModal.title),
            description: getModalsDescription(TRANSLATION_COLLABORATORS.table.resendEmailModal.description),
            icon: <HappyBuddy />,
            loading: loading,
            primaryAction: {
              label: t(TRANSLATION_COLLABORATORS.table.resendEmailModal.buttonSend),
              onClick: resendEmail
            },
            onClose: closeModal
          };
        case 'insuranceCertificate':
          return {
            open: modalType === type,
            title: t(CERTIFICATE_NOT_FOUND_MODAL.title as string),
            description: t(CERTIFICATE_NOT_FOUND_MODAL.description as string),
            icon: <CERTIFICATE_NOT_FOUND_MODAL.icon />,
            loading: loading,
            reverseButtons: true,
            primaryAction: {
              label: t(CERTIFICATE_NOT_FOUND_MODAL.primaryActionText as string),
              onClick: openIntercom
            },
            secondaryAction: {
              label: t(CERTIFICATE_NOT_FOUND_MODAL.secondaryActionText as string),
              onClick: closeModal
            }
          };
      }
    },
    [modalType, loading, closeModal, t, getModalsDescription, onDelete, resendEmail, openIntercom]
  );

  return (
    <div className="collaborators-table-actions" onClick={handleCellClick} role="presentation">
      {setActionButton()}
      {isOptionsMenuOpen && (
        <CollaboratorsActionsMenu
          showMenu={isOptionsMenuOpen}
          setShowMenu={setIsOptionsMenuOpen}
          member={member}
          companyId={company.id}
          hasNovationOption={hasNovationOption}
          hasInsuranceCertificate={hasInsuranceCertificate}
          loadingCertificates={loadingCertificates}
          certificates={certificates}
          onOptionClick={handleMenuOptions}
          onCertificateDownload={handleCertificateDownload}
          className="collaborators-actions-column"
          disableOutsideClick={modalType !== null}
        />
      )}
      <BdsModal {...getModalProps('delete')} />
      <BdsModal {...getModalProps('resendEmail')} />
      <BdsModal {...getModalProps('insuranceCertificate')} />
      {openToast && (
        <Toast
          title={t(toastData.title)}
          icon={toastData.icon}
          open={openToast}
          button={toastData.button}
          handleClose={handleCloseToast}
        />
      )}
      <LoadingModal
        showModal={loadingNovation}
        description={t(TRANSLATION_COLLABORATORS.table.menu.novation.loadingModal)}
      />
    </div>
  );
};

export default CollaboratorsActions;
